'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

/**
 * Launch-flow entrance transition.
 *
 * The lobby (`/`) and the `/create` wizard share a client layout
 * that mounts this provider. When the user clicks "Create a Board" (or the
 * "Create" nav entry), we play a deliberately-restrained transition:
 *
 *  1. A small gold halo pulses once from the click origin (the button
 *     "acknowledges" itself, spatially).
 *  2. The lobby gently recedes — a hair of scale, a whisper of blur, and a
 *     quick fade.
 *  3. We kick `router.push(TARGET_PATH)` in the same tick so the route
 *     load happens *in parallel* with the halo / receding lobby (not
 *     after). The halo is visible across the route change because the
 *     provider lives above the route in the layout tree.
 *  4. The wizard fades in from a matching almost-there state.
 *
 * ## Snappy-feel tuning
 *
 * Earlier versions waited ~220ms before calling `router.push`, which
 * meant users felt a hard stall between the click and the navigation.
 * We now:
 *   - prefetch `TARGET_PATH` as soon as the provider mounts (and also
 *     whenever `begin()` is called, as a belt-and-braces), so the
 *     Next.js route loader has the chunks ready;
 *   - fire `router.push` immediately on `begin()` — the visual
 *     transition plays concurrently with the navigation instead of
 *     gating it;
 *   - shrink the post-arrival settle from 180ms → 90ms so we don't
 *     linger on top of the wizard's own enter animation.
 *
 * Respects `prefers-reduced-motion` — skips the visuals entirely.
 */

interface Origin {
  x: number;
  y: number;
}

interface LaunchTransitionContextValue {
  /**
   * Begin the transition and route to the wizard. Pass the click event so the
   * flare originates from the button; omit for an origin at screen centre.
   */
  begin: (event?: { clientX: number; clientY: number } | Origin) => void;
  active: boolean;
}

const LaunchTransitionContext = createContext<LaunchTransitionContextValue | null>(
  null,
);

export function useLaunchTransition(): LaunchTransitionContextValue {
  const ctx = useContext(LaunchTransitionContext);
  if (ctx) return ctx;
  // Safe no-op so callers outside the provider (e.g. SSR probes) don't crash.
  return { begin: () => {}, active: false };
}

/** Post-arrival dwell — gives the wizard one paint beat to settle before
 *  we fade the overlay. Shorter than the pre-fix value (180ms) because
 *  `router.push` is now fired immediately so the page is usually painted
 *  well before the halo finishes. */
const SETTLE_MS = 90;
const TARGET_PATH = '/create';

export function LaunchTransitionProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const reduceMotion = useReducedMotion();

  const [active, setActive] = useState(false);
  const [origin, setOrigin] = useState<Origin | null>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prefetchedRef = useRef(false);

  const clear = useCallback(() => {
    if (settleTimer.current) {
      clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
  }, []);

  useEffect(() => clear, [clear]);

  // Warm the launch route as soon as the provider mounts, unless we're
  // already on it. This lets `router.push` complete with cached RSC +
  // chunks, which is the biggest single contributor to the old "laggy"
  // feel. `router.prefetch` is a no-op in dev for pages that are already
  // compiled, and is already internally de-duped, but we still guard with
  // a ref so a pathname change doesn't trigger needless re-prefetches.
  useEffect(() => {
    if (prefetchedRef.current) return;
    if (pathname === TARGET_PATH) return;
    prefetchedRef.current = true;
    try {
      router.prefetch(TARGET_PATH);
    } catch {
      // Prefetch is best-effort; failing is fine, it just means the
      // navigation will fall back to a cold route load.
    }
  }, [pathname, router]);

  // Once we've arrived at the wizard, fade the overlay out after it has had a
  // beat to cover the route change. This keeps the gold flare visible *across*
  // the navigation rather than cutting at push time.
  useEffect(() => {
    if (!active) return;
    if (pathname !== TARGET_PATH) return;
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      setActive(false);
      setOrigin(null);
    }, SETTLE_MS);
    return () => {
      if (settleTimer.current) clearTimeout(settleTimer.current);
    };
  }, [active, pathname]);

  const begin = useCallback(
    (input?: { clientX: number; clientY: number } | Origin) => {
      if (active) return;
      if (pathname === TARGET_PATH) return; // already there

      // Honour reduced motion — skip the visuals entirely.
      if (reduceMotion) {
        router.push(TARGET_PATH);
        return;
      }

      let next: Origin | null = null;
      if (input) {
        if ('clientX' in input) {
          next = { x: input.clientX, y: input.clientY };
        } else {
          next = { x: input.x, y: input.y };
        }
      }
      setOrigin(next);
      setActive(true);

      // Prefetch is best-effort — if the user clicked before the mount
      // prefetch landed (rare), re-arm it here so `push` has a chance to
      // use a warm cache. No-op if already prefetched / compiled.
      try {
        router.prefetch(TARGET_PATH);
      } catch {
        // ignore
      }

      // Kick the navigation in the same tick so route loading runs in
      // parallel with the halo + lobby transform. The overlay stays up
      // across the route change because the provider sits above the
      // route in the layout tree.
      router.push(TARGET_PATH);
    },
    [active, pathname, reduceMotion, router],
  );

  const isDiving = active && pathname !== TARGET_PATH;

  return (
    <LaunchTransitionContext.Provider value={{ begin, active }}>
      {/* Stable, non-transforming clip layer. Chrome counts a transformed
          element's scaled bounding box toward its parent's scrollable
          overflow, so we must put the scale on an *inner* div and let a
          static `overflow:hidden` wrapper swallow the bleed. Otherwise the
          document briefly grows by a couple of pixels and scrollbars flash. */}
      <div className="h-full w-full overflow-hidden">
        <motion.div
          className="h-full w-full"
          // Transform + opacity only — both cheap GPU compositor ops. No
          // `filter: blur()`: over a live R3F canvas that's a full-page
          // repaint every frame, and the blur halo also bleeds beyond the
          // element, re-triggering the scrollbar issue above.
          style={{ willChange: isDiving ? 'transform, opacity' : 'auto' }}
          animate={
            isDiving
              ? { scale: 1.012, opacity: 0.76 }
              : { scale: 1, opacity: 1 }
          }
          // Snappy ease-out on the dive: gets most of the recede done in
          // the first ~80ms so the click feels acknowledged instantly,
          // then glides into place for the remaining ~100ms. The old
          // ease-in-out started too slow and masked the router push that
          // was already running behind it.
          transition={{ duration: 0.18, ease: [0.22, 0.7, 0.3, 1] }}
        >
          {children}
        </motion.div>
      </div>
      <LaunchHaloPulse active={isDiving} origin={origin} />
    </LaunchTransitionContext.Provider>
  );
}

/**
 * A single compact halo that pulses once at the click origin — the
 * transition's only overlay element. ~220ms, local to the button, no
 * full-screen washes or streaks.
 */
function LaunchHaloPulse({
  active,
  origin,
}: {
  active: boolean;
  origin: Origin | null;
}) {
  if (!origin) return null;
  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key="launch-halo"
          aria-hidden
          className="pointer-events-none fixed z-[9998]"
          style={{
            left: origin.x,
            top: origin.y,
            transform: 'translate(-50%, -50%)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12, ease: 'easeOut' }}
        >
          {/* Soft core glow — a quick gold breath, no more. */}
          <motion.div
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 2.2, opacity: [0, 0.55, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.36, ease: [0.22, 0.7, 0.35, 1] }}
            className="h-24 w-24 rounded-full"
            style={{
              background:
                'radial-gradient(circle, rgba(255,220,150,0.85) 0%, rgba(255,180,80,0.35) 45%, rgba(0,0,0,0) 72%)',
              filter: 'blur(1px)',
            }}
          />
          {/* Thin expanding ring — reads as "press registered" rather than
              "warp drive engaged". */}
          <motion.div
            initial={{ scale: 0.3, opacity: 0 }}
            animate={{ scale: 3, opacity: [0, 0.8, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.44, ease: [0.22, 0.7, 0.35, 1] }}
            className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              border: '1px solid rgba(255,214,140,0.8)',
              boxShadow: '0 0 14px rgba(255,200,110,0.5)',
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
